/**
 * Entry IIFE / global browser.
 *
 * Global público: `window.Pixel(settings)` — factory callable.
 * Compatibilidade: `window.PixelV2`.
 *
 * Carregar o bundle apenas registra a API. Não há auto-init, leitura de
 * `document.currentScript` nem atributos `data-*`.
 *
 * Não importe este módulo no SSR. O entry principal e `@beworke/pixel/browser`
 * não têm este side effect.
 */

import { DEFAULT_PIXEL_CONFIG } from '../config/defaults/pixel-default-config';
import { createPixel } from '../pixel/createPixel';
import { Pixel as PixelClient } from '../pixel/Pixel';
import { resolveStorageAdapterName } from '../services/storage/StorageFactory';
import {
  bootstrapPixel,
  destroyBootstrappedPixel,
  getBootstrappedPixel,
  getBootstrappedPixelPending,
} from './bootstrap';
import type { PixelBrowserFactory } from './types';

const PIXEL_FACTORY_MARK = '__beworkePixelFactory__' as const;

export type PixelV2GlobalApi = {
  Pixel: typeof PixelClient;
  DEFAULT_PIXEL_CONFIG: typeof DEFAULT_PIXEL_CONFIG;
  resolveStorageAdapterName: typeof resolveStorageAdapterName;
  createPixel: typeof createPixel;
  bootstrap: typeof bootstrapPixel;
  getInstance: typeof getBootstrappedPixel;
  destroy: typeof destroyBootstrappedPixel;
};

declare global {
  interface Window {
    Pixel?: PixelBrowserFactory;
    PixelV2?: PixelV2GlobalApi;
  }
}

function isOwnPixelFactory(value: unknown): value is PixelBrowserFactory {
  return (
    typeof value === 'function' &&
    (value as { [PIXEL_FACTORY_MARK]?: boolean })[PIXEL_FACTORY_MARK] === true
  );
}

export function createPixelBrowserFactory(): PixelBrowserFactory {
  const factory = Object.assign(
    function pixelFactory(
      settings: Parameters<PixelBrowserFactory>[0]
    ): ReturnType<PixelBrowserFactory> {
      return bootstrapPixel(settings);
    },
    {
      create: createPixel,
      bootstrap: bootstrapPixel,
      getInstance: getBootstrappedPixel,
      getPending: getBootstrappedPixelPending,
      destroy: destroyBootstrappedPixel,
      Client: PixelClient,
      DEFAULT_PIXEL_CONFIG,
    }
  ) as PixelBrowserFactory;

  Object.defineProperty(factory, PIXEL_FACTORY_MARK, {
    value: true,
    enumerable: false,
  });

  return factory;
}

export const pixelBrowserFactory: PixelBrowserFactory =
  createPixelBrowserFactory();

export const pixelV2GlobalApi: PixelV2GlobalApi = {
  Pixel: PixelClient,
  DEFAULT_PIXEL_CONFIG,
  resolveStorageAdapterName,
  createPixel,
  bootstrap: bootstrapPixel,
  getInstance: getBootstrappedPixel,
  destroy: destroyBootstrappedPixel,
};

function reportForeignPixel(existing: unknown): void {
  const description =
    typeof existing === 'function'
      ? existing.name || 'function'
      : typeof existing;
  // eslint-disable-next-line no-console
  console.error(
    `[pixel] window.Pixel já existe (${description}) e não pertence ao @beworke/pixel. O bundle não será registrado.`
  );
}

function ensurePixelV2(): void {
  const previousV2 = window.PixelV2;
  if (previousV2 && previousV2 !== pixelV2GlobalApi) {
    window.PixelV2 = {
      ...previousV2,
      Pixel: previousV2.Pixel ?? PixelClient,
      DEFAULT_PIXEL_CONFIG:
        previousV2.DEFAULT_PIXEL_CONFIG ?? DEFAULT_PIXEL_CONFIG,
      resolveStorageAdapterName:
        previousV2.resolveStorageAdapterName ?? resolveStorageAdapterName,
      createPixel,
      bootstrap: bootstrapPixel,
      getInstance: getBootstrappedPixel,
      destroy: destroyBootstrappedPixel,
    };
    return;
  }

  window.PixelV2 = pixelV2GlobalApi;
}

/**
 * Registra `window.Pixel` e estende `window.PixelV2`.
 * Idempotente para recarregamentos do próprio bundle.
 * @returns a factory instalada, ou `null` se um global estrangeiro bloquear.
 */
export function installPixelGlobals(): PixelBrowserFactory | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const existing = window.Pixel;
  if (isOwnPixelFactory(existing)) {
    ensurePixelV2();
    return existing;
  }

  if (existing !== undefined) {
    reportForeignPixel(existing);
    ensurePixelV2();
    return null;
  }

  window.Pixel = pixelBrowserFactory;
  ensurePixelV2();
  return pixelBrowserFactory;
}

installPixelGlobals();

export {
  PixelClient as Pixel,
  createPixel,
  bootstrapPixel,
  destroyBootstrappedPixel,
  getBootstrappedPixel,
  DEFAULT_PIXEL_CONFIG,
  resolveStorageAdapterName,
};
